import {get} from 'svelte/store';
import md5 from 'blueimp-md5';
import {getCookie} from 'general/libs/getCookie';
import {setCookie} from 'general/libs/setCookie';

import type {DataLayerEventType} from 'types/analytics';
import {analytics, modal} from 'general/store';

/**
 * Push event to dataLayer
 * @param {DataLayerEventType} event - GA event
 */
export const pushDataLayer = (event: DataLayerEventType): void => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
};
/**
 * format string replacing variables with source values
 * @param {string} template - string where replacement will take place
 * @param {Map<string, string>} source - values for replacement
 * @returns {string} - replaced string
 */
export const formatString = (template: string, source: {[key: string]: string}): string => {
    return Object.entries(source).reduce(
        (acc, [key, value]) => acc.replace(`{{${key}}}`, value),
        template,
    );
};

type OptionalDataType = {
    email?: string;
    number?: string;
};

/**
 * Push popup event to datalayer
 * @param {string} eventLabel - event label
 * @param {OptionalDataType} optionalData - email or phone number
 */
export const pushPopupEvent = (
    eventLabel: string,
    optionalData: OptionalDataType = {},
    isInteraction = false,
): void => {
    const {email} = optionalData;
    const {popupType = 'welcome'} = get(modal);
    const source = {type: popupType as string};

    const filledData = email ? {filled_email: md5(email)} : {};

    pushDataLayer({
        event: isInteraction
            ? 'GAEventGenericTriggerInteraction'
            : 'GAEventGenericTriggerNonInteraction',
        eventCategory: 'popups',
        eventAction: get(analytics).action || '',
        eventLabel: formatString(eventLabel, source),
        ...filledData,
    });
};

/**
 * Send analytics event depending on numbers of events per day
 * @param {string} label - event label
 * @param {number} eventCount - count of events per day
 * @param {OptionalDataType} optionalData - email or phone number
 */
export const sendAnalyticsPerDay = (
    label: string,
    eventCount = 1,
    optionalData: OptionalDataType = {},
): void => {
    const eventKey = get(analytics).action?.replace(/\s/g, '') || '';

    const alreadySentCount = Number(getCookie(eventKey)) || 0;

    if (alreadySentCount < eventCount) {
        pushPopupEvent(label, optionalData);
        setCookie(eventKey, String(alreadySentCount + 1), 1);
    }
};
